import React, {createContext, useCallback, useContext, useMemo, useReducer, useRef} from "react";

import {checkIfAnimated} from "libs/bg-utils";
import {sceneColorRecords, fieldColorsSchema, elementColorsSchema} from "libs/sceneColors";
import {normalizeButtonColors} from "libs/bannerButton";
import {normalizeCodeHighlights} from "libs/codeHighlight";
import {withBakedCrops} from "libs/bakedBg";

// Single source of truth for the bg/upload/logo image slice of a scene.
// Same architecture as src/contexts/edits.js — adding a field is a one-place
// change here:
//   - record (Record<id, value>)  →  add to INITIAL_STATE + IMAGE_RECORD_FIELDS
//   - scalar that round-trips     →  add to INITIAL_STATE + SCALAR_SCENE_FIELDS
//   - scalar that doesn't         →  add to INITIAL_STATE only (transient)
//
// The shape used to live in five hand-maintained lists (state hooks, reset,
// loadScene, App.buildScene's body, App.buildScene's dep array). Adding a
// field meant editing all five; missing one silently lost data on save/load.
// IMAGES_SCENE_FIELDS below is the authoritative list — buildScene iterates
// it and loadScene consumes it, so they cannot drift.
export const INITIAL_IMAGES_STATE = Object.freeze({
  // Records (Record<templateId, value>)
  uploadImages: {},
  customBgs: {},
  // Per-template marker: true when customBgs[id] holds a USER UPLOAD rather than
  // the seeded per-frame baked default. Kept separate from the bg transform
  // fields because baked defaults are now pan/zoom-editable too (they carry a
  // transform once touched), so transform presence can no longer signal "custom
  // upload". Drives the Livestream CustomBgUpload remove button.
  customBgUploaded: {},
  bgX: {},
  bgY: {},
  bgScale: {},
  bgRotate: {},
  bgFlip: {},
  uploadX: {},
  uploadY: {},
  uploadScale: {},
  uploadRotate: {},
  uploadFlip: {},
  logoX: {},
  logoY: {},
  logoScale: {},
  logos: {},
  logoVariants: {},
  textColorLocked: {},
  fieldColors: {},
  elementColors: {},
  // Per-template color of the banner button, "light" or "dark"
  // (libs/bannerButton.ts). Absent for a button with its default look.
  buttonColors: {},
  // Per-template code highlight over a screenshot (libs/codeHighlight.ts):
  // {enabled, x, y, width, height, color}. Absent for a banner that shows the
  // highlight of the design.
  codeHighlights: {},
  // Per-template background opacity and brightness, in percent, keyed by
  // template id: a detached template's own value, or a preset an admin saved
  // into the default scene. Absent for a template that follows the shared
  // `bgOpacity` / `bgBrightness`.
  bgOpacities: {},
  bgBrightnesses: {},
  // The per-template presets the applied default scene carried
  // ({customBg, opacity, brightness} per template id) — what re-attaching a
  // template returns it to. See layout/BgTransformer/templateBg.ts.
  bgPresets: {},
  // Baked backgrounds (libs/bakedBg.ts): per template id, the BakedBgEntry of
  // the image the default scene cut to the banner's box for `bakedBgOption`.
  // A baked banner shows it with no transform while it shows the cut's crop.
  bakedBgs: {},

  // Scene-roundtripped scalars
  customBg: "",
  bgBrightness: 100,
  bgBlurValue: 0,
  // Percent, like bgBrightness. 100 is opaque; the JetBrains Air designs sit
  // their artwork at 70 over the base colour, which is where this came from.
  bgOpacity: 100,
  logoHidden: false,
  // The bgOption `bakedBgs` belongs to; null when there are none.
  bakedBgOption: null,

  // Transient UI state (not part of saved scenes)
  isBgAnimation: false,
  isUploadAnimation: false,
  // Phase-2 background-transform derivation (issue #528, 2a). Holds ONLY user
  // intent — explicit drags / tile-picks this session — keyed by
  // [bgOption][templateId] -> {x,y,scale,rotate,flip}. NOT round-tripped in
  // scenes: buildScene composes bgX/bgY/... from the effective transform (which
  // reads this as its highest-precedence layer). Cleared on RESET (scene nav),
  // like the other transient fields. Only written/read when the bgDerivation
  // flag is on; inert otherwise.
  userBgAdjustments: {},
  // The templates of `userBgAdjustments`, per bgOption, in the order of their
  // last change (the newest last). The background selector draws its crop
  // previews on the newest horizontal one, and a default-scene save records it.
  bgAdjustmentOrder: {},
  // Number of image uploads currently in flight. Save is blocked while > 0
  // so we never persist a `blob:` URL (which only lives in the originating
  // tab) into scene state. Incremented when a dropzone starts an upload,
  // decremented on completion / error. See #169.
  pendingUploadCount: 0,

  // Cache buster for the upload caption — bumped on real-image writes
  // (see EDIT_LOCKED_RECORD case). Intentionally NOT reset on RESET so
  // consumers keying off it stay stable across scene navigation.
  uploadImageVersion: 0,
});

// Records keyed by template id. Treated identically by the generic edit /
// load paths — useful as a data-driven loop to avoid 18 hand-rolled cases.
export const IMAGE_RECORD_FIELDS = Object.freeze([
  "uploadImages",
  "customBgs",
  "customBgUploaded",
  "bgX",
  "bgY",
  "bgScale",
  "bgRotate",
  "bgFlip",
  "uploadX",
  "uploadY",
  "uploadScale",
  "uploadRotate",
  "uploadFlip",
  "logoX",
  "logoY",
  "logoScale",
  "logos",
  "logoVariants",
  "textColorLocked",
  "fieldColors",
  "elementColors",
  "buttonColors",
  "codeHighlights",
  "bgOpacities",
  "bgBrightnesses",
  "bgPresets",
  "bakedBgs",
]);

// Scalars that round-trip in scene save/load.
export const SCALAR_SCENE_FIELDS = Object.freeze([
  "customBg",
  "bgBrightness",
  "bgBlurValue",
  "bgOpacity",
  "logoHidden",
  "bakedBgOption",
]);

// Authoritative roundtrip set — used so buildScene and loadScene agree by
// construction, not by hand-maintained lists.
export const IMAGES_SCENE_FIELDS = Object.freeze([...IMAGE_RECORD_FIELDS, ...SCALAR_SCENE_FIELDS]);

const RECORD_FIELD_SET = new Set(IMAGE_RECORD_FIELDS);

/**
 * Hold an expensive record edit outside React state until its debounced write.
 * Scene construction can still read the edit synchronously. This prevents a
 * Save click from capturing the previous committed value.
 */
export function createPendingSceneRecord() {
  let resolver = null;

  return {
    stage(nextResolver) {
      if (typeof nextResolver !== "function") {
        throw new Error("Pending scene record resolver must be a function");
      }
      resolver = nextResolver;
    },
    read(committedValue) {
      return resolver ? resolver(committedValue) : committedValue;
    },
    clear() {
      resolver = null;
    },
  };
}

/** Read one image-owned field for scene construction. */
export function readImageSceneField(imagesContext, field) {
  if (field !== "customBgs") return imagesContext[field];
  if (typeof imagesContext.readCustomBgsForScene !== "function") {
    throw new Error("Images context is missing readCustomBgsForScene");
  }
  return imagesContext.readCustomBgsForScene();
}

// Scalar fields SET_SCALAR is allowed to write. Excludes uploadImageVersion
// intentionally — it's a cache-buster counter preserved across RESET, and
// routing it through SET_SCALAR would let a future caller silently bypass
// that protection. The named wrappers below use field literals from this set.
// `pendingUploadCount` deliberately NOT in this set — it's a clamped counter
// whose only write path is BUMP_PENDING_UPLOADS. Routing it through
// SET_SCALAR would let a caller silently bypass the Math.max(0, …) clamp and
// flip the counter negative, which would temporarily unblock save while
// uploads are still in flight (persisting a `blob:` URL into scene state).
const SCALAR_FIELD_SET = new Set([...SCALAR_SCENE_FIELDS, "isBgAnimation", "isUploadAnimation"]);

// Bg / upload / logo "transform" actions write multiple record fields
// atomically. Centralized here so the reducer cases stay short.
// BG_TRANSFORM_FIELDS is also exported because useScene needs to strip
// these from a saved-scene payload when the URL `?background=` overrides
// the saved bgOption — saved transforms are bound to the saved bg and
// don't apply to a different one.
export const BG_TRANSFORM_FIELDS = ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip"];
const UPLOAD_TRANSFORM_FIELDS = ["uploadX", "uploadY", "uploadScale", "uploadRotate", "uploadFlip"];
const LOGO_TRANSFORM_FIELDS = ["logoX", "logoY", "logoScale"];

export function imagesReducer(state, action) {
  switch (action.type) {
    case "RESET": {
      // Preserve the upload-version cache buster across scene navigation —
      // resetting it would force every consumer keying off it to re-render
      // through the cache-busted path, which is the opposite of what scene
      // navigation should do.
      return {...INITIAL_IMAGES_STATE, uploadImageVersion: state.uploadImageVersion};
    }

    case "LOAD_SCENE": {
      const {data} = action;
      const next = {...state};

      // Legacy: scenes saved before the field was renamed used `images` for
      // what is now `uploadImages`. Both keys can co-exist on mixed-vintage
      // payloads — the modern key wins.
      if (data.images !== undefined) next.uploadImages = data.images;
      if (data.uploadImages !== undefined) next.uploadImages = data.uploadImages;

      if (data.customBg !== undefined) next.customBg = data.customBg;
      if (data.customBgs !== undefined) next.customBgs = data.customBgs || {};
      if (data.customBgUploaded !== undefined) next.customBgUploaded = data.customBgUploaded || {};
      if (data.logos !== undefined) next.logos = data.logos;

      // logoVariants and textColorLocked default to {} when absent — every
      // other record stays at its previous value. Preserved to match the
      // pre-refactor loadScene behavior.
      next.logoVariants = data.logoVariants !== undefined ? data.logoVariants || {} : {};
      next.textColorLocked = data.textColorLocked !== undefined ? data.textColorLocked || {} : {};
      next.buttonColors = normalizeButtonColors(data.buttonColors);
      next.codeHighlights = normalizeCodeHighlights(data.codeHighlights);
      next.fieldColors = sceneColorRecords(data.fieldColors, fieldColorsSchema);
      next.elementColors = sceneColorRecords(data.elementColors, elementColorsSchema);
      next.bgOpacities = data.bgOpacities !== undefined ? data.bgOpacities || {} : {};
      next.bgBrightnesses = data.bgBrightnesses !== undefined ? data.bgBrightnesses || {} : {};
      next.bgPresets = data.bgPresets !== undefined ? data.bgPresets || {} : {};
      // A scene saved before baked backgrounds has none: clear them.
      next.bakedBgs = withBakedCrops(data.bakedBgs, data);
      next.bakedBgOption = data.bakedBgs ? (data.bakedBgOption ?? null) : null;

      // Group-load: bg/upload/logo transforms apply ALL members atomically
      // when ANY member is present. Missing members are written as
      // undefined — pre-refactor behavior we preserve so consumers that
      // tolerated `bgX === undefined` keep working. (Yes, that is brittle;
      // tightening it is a separate refactor.)
      const hasBgTransform = BG_TRANSFORM_FIELDS.some((f) => data[f] !== undefined);
      if (hasBgTransform) {
        for (const f of BG_TRANSFORM_FIELDS) next[f] = data[f];
      }
      const hasUploadTransform = UPLOAD_TRANSFORM_FIELDS.some((f) => data[f] !== undefined);
      if (hasUploadTransform) {
        for (const f of UPLOAD_TRANSFORM_FIELDS) next[f] = data[f];
      }
      const hasLogoTransform = LOGO_TRANSFORM_FIELDS.some((f) => data[f] !== undefined);
      if (hasLogoTransform) {
        for (const f of LOGO_TRANSFORM_FIELDS) next[f] = data[f];
      }

      next.logoHidden = data.logoHidden !== undefined ? !!data.logoHidden : false;

      if (data.bgBrightness !== undefined) next.bgBrightness = data.bgBrightness;
      if (data.bgBlurValue !== undefined) next.bgBlurValue = data.bgBlurValue;
      if (data.bgOpacity !== undefined) next.bgOpacity = data.bgOpacity;

      // setIsBgAnimation is computed asynchronously by the wrapper — see
      // EditsContextProvider.loadScene below. Synchronously zero it here
      // so a scene without a custom bg can't carry a stale animation flag.
      next.isBgAnimation = false;

      return next;
    }

    case "EDIT_RECORD": {
      const {field, id, value} = action;
      if (!RECORD_FIELD_SET.has(field)) {
        throw new Error(`EDIT_RECORD: unknown field "${field}"`);
      }
      return {...state, [field]: {...state[field], [id]: value}};
    }

    case "EDIT_LOCKED_RECORD": {
      const {field, id, value, isLocked} = action;
      if (!RECORD_FIELD_SET.has(field)) {
        throw new Error(`EDIT_LOCKED_RECORD: unknown field "${field}"`);
      }
      const currentId = isLocked ? id : "main";
      const next = {
        ...state,
        [field]: {...state[field], [currentId]: value},
      };
      // Bumping the cache-buster on real-image writes lets consumers key
      // off it instead of doing structural compare on uploadImages. Skip
      // for null/undefined removals.
      if (field === "uploadImages" && value) {
        next.uploadImageVersion = state.uploadImageVersion + 1;
      }
      return next;
    }

    case "LOAD_RECORD": {
      const {field, value} = action;
      if (!RECORD_FIELD_SET.has(field)) {
        throw new Error(`LOAD_RECORD: unknown field "${field}"`);
      }
      return {...state, [field]: value || {}};
    }

    case "LOAD_BAKED_BGS": {
      const bakedBgs = action.bakedBgs || {};
      const empty = Object.keys(bakedBgs).length === 0;
      return {
        ...state,
        bakedBgs,
        bakedBgOption: empty || action.option == null ? null : String(action.option),
      };
    }

    case "SET_SCALAR":
      if (!SCALAR_FIELD_SET.has(action.field)) {
        throw new Error(`SET_SCALAR: unknown or protected field "${action.field}"`);
      }
      return {...state, [action.field]: action.value};

    case "BUMP_PENDING_UPLOADS":
      // Race-safe inc/dec for concurrent uploads — reading the value via a
      // closure-captured scalar setter would mis-account when two uploads
      // overlap. Clamped at 0 so an extra dec (e.g. after a manual reset)
      // can't flip the counter negative and unblock saves while uploads
      // are still in flight.
      return {
        ...state,
        pendingUploadCount: Math.max(0, state.pendingUploadCount + action.delta),
      };

    case "EDIT_BG_TRANSFORM": {
      const {x, y, s, r, f} = action;
      return {...state, bgX: x, bgY: y, bgScale: s, bgRotate: r, bgFlip: f};
    }

    case "SET_USER_BG_ADJUSTMENT": {
      // Record one template's user intent for the current background option
      // (issue #528, 2a). Per-(bgOption, templateId) so switching backgrounds
      // preserves each option's manual tweaks independently.
      const {bgOption, templateId, transform} = action;
      const key = String(bgOption);
      return {
        ...state,
        userBgAdjustments: {
          ...state.userBgAdjustments,
          [key]: {...(state.userBgAdjustments[key] || {}), [templateId]: transform},
        },
        bgAdjustmentOrder: {
          ...state.bgAdjustmentOrder,
          [key]: [
            ...(state.bgAdjustmentOrder[key] || []).filter((id) => id !== templateId),
            templateId,
          ],
        },
      };
    }

    case "CLEAR_USER_BG_ADJUSTMENT": {
      // Drop one template's user intent for a background option, so the
      // derived chain (resolved scene, then catalog) shows through again:
      // the "Reset to default" button of the background tools.
      const {bgOption, templateId} = action;
      const key = String(bgOption);
      if (!state.userBgAdjustments[key]?.[templateId]) return state;
      const {[templateId]: _dropped, ...rest} = state.userBgAdjustments[key];
      return {
        ...state,
        userBgAdjustments: {...state.userBgAdjustments, [key]: rest},
        bgAdjustmentOrder: {
          ...state.bgAdjustmentOrder,
          [key]: (state.bgAdjustmentOrder[key] || []).filter((id) => id !== templateId),
        },
      };
    }

    case "EDIT_UPLOAD_TRANSFORM": {
      const {x, y, s, r, f} = action;
      return {
        ...state,
        uploadX: x,
        uploadY: y,
        uploadScale: s,
        uploadRotate: r,
        uploadFlip: f,
      };
    }

    case "EDIT_LOGO_TRANSFORM": {
      const {x, y, s} = action;
      return {...state, logoX: x, logoY: y, logoScale: s};
    }

    default:
      return state;
  }
}

const NOOP = () => {};

/**
 * Build an editLockedRecord wrapper bound to a specific field. Pulled out of
 * the provider so the arg-order contract `(id, value, isLocked)` can be unit-
 * tested directly. Three wrappers go through this factory — `editLogos`,
 * `editCustomBgs`, `editUploadImages` — and a regression in the parameter
 * order silently corrupts data for all of them (#339).
 */
export function makeEditLockedRecord(dispatch, field) {
  return (id, value, isLocked = false) =>
    dispatch({type: "EDIT_LOCKED_RECORD", field, id, value, isLocked});
}

export const Images = createContext({
  ...INITIAL_IMAGES_STATE,
  reset: NOOP,
  loadScene: NOOP,
  editLogoHidden: NOOP,
  editLogoVariant: NOOP,
  editTextColorLocked: NOOP,
  editButtonColor: NOOP,
  editCodeHighlight: NOOP,
  editLogos: NOOP,
  editLogoY: NOOP,
  editLogoX: NOOP,
  editLogoScale: NOOP,
  editLogoTransform: NOOP,
  loadLogos: NOOP,
  loadLogoVariants: NOOP,
  loadTextColorLocked: NOOP,
  loadButtonColors: NOOP,
  loadImages: NOOP,
  loadCustomBgs: NOOP,
  stageCustomBgsSceneDraft: NOOP,
  clearCustomBgsSceneDraft: NOOP,
  commitCustomBgsSceneDraft: NOOP,
  readCustomBgsForScene: () => ({}),
  removeImages: NOOP,
  editCustomBg: NOOP,
  editUploadImages: NOOP,
  editCustomBgs: NOOP,
  editCustomBgUploaded: NOOP,
  editTransform: NOOP,
  setUserBgAdjustment: NOOP,
  clearUserBgAdjustment: NOOP,
  editBgScale: NOOP,
  editBgY: NOOP,
  editBgX: NOOP,
  editUploadTransform: NOOP,
  editUploadScale: NOOP,
  editUploadY: NOOP,
  editUploadX: NOOP,
  removeUploadImages: NOOP,
  editBgBlurValue: NOOP,
  editBgOpacities: NOOP,
  editBgBrightnesses: NOOP,
  loadBgOpacities: NOOP,
  loadBgBrightnesses: NOOP,
  loadBgPresets: NOOP,
  loadCustomBgUploaded: NOOP,
  editBgFlip: NOOP,
  editBgRotate: NOOP,
  editUploadFlip: NOOP,
  editUploadRotate: NOOP,
  editBgBrightness: NOOP,
  editIsBgAnimation: NOOP,
  editIsUploadAnimation: NOOP,
});

export const ImagesContextProvider = ({children}) => {
  const [state, dispatch] = useReducer(imagesReducer, INITIAL_IMAGES_STATE);
  const stateRef = useRef(state);
  const pendingCustomBgsRef = useRef(createPendingSceneRecord());
  stateRef.current = state;

  const stageCustomBgsSceneDraft = useCallback((resolver) => {
    pendingCustomBgsRef.current.stage(resolver);
  }, []);
  const clearCustomBgsSceneDraft = useCallback(() => {
    pendingCustomBgsRef.current.clear();
  }, []);
  const readCustomBgsForScene = useCallback(
    () => pendingCustomBgsRef.current.read(stateRef.current.customBgs),
    []
  );
  const commitCustomBgsSceneDraft = useCallback(() => {
    const backgrounds = pendingCustomBgsRef.current.read(stateRef.current.customBgs);
    pendingCustomBgsRef.current.clear();
    // Keep synchronous scene reads current until React processes the dispatch.
    stateRef.current = {...stateRef.current, customBgs: backgrounds};
    dispatch({type: "LOAD_RECORD", field: "customBgs", value: backgrounds});
    return backgrounds;
  }, []);

  // dispatch is stable across renders; useCallback([]) wrappers below have
  // stable identity for consumer effect deps.
  const reset = useCallback(() => {
    pendingCustomBgsRef.current.clear();
    dispatch({type: "RESET"});
  }, []);

  const loadScene = useCallback((data) => {
    pendingCustomBgsRef.current.clear();
    dispatch({type: "LOAD_SCENE", data});
    // checkIfAnimated is async — fire it after the synchronous load and
    // dispatch the result as a follow-up. The reducer's LOAD_SCENE already
    // zeroed isBgAnimation, so a slow check can't race with the next scene.
    if (data.customBg !== undefined && data.customBg) {
      (async () => {
        const isAnimated = await checkIfAnimated(data.customBg);
        dispatch({type: "SET_SCALAR", field: "isBgAnimation", value: isAnimated});
      })();
    }
  }, []);

  const editScalar = useCallback(
    (field) => (value) => dispatch({type: "SET_SCALAR", field, value}),
    []
  );
  const editRecord = useCallback(
    (field) => (id, value) => dispatch({type: "EDIT_RECORD", field, id, value}),
    []
  );
  const editLockedRecord = useCallback((field) => makeEditLockedRecord(dispatch, field), []);
  const editCustomBgs = useCallback((id, value, isLocked = false) => {
    pendingCustomBgsRef.current.clear();
    makeEditLockedRecord(dispatch, "customBgs")(id, value, isLocked);
  }, []);

  // Per-field public-API wrappers. The factories above remove ~150 lines
  // of near-duplicated `setX(cur => ({...cur, [id]: value}))` boilerplate
  // while keeping each public name grep-able from consumer call sites.
  const wrappers = useMemo(() => {
    return {
      // Bg transforms
      editBgX: editRecord("bgX"),
      editBgY: editRecord("bgY"),
      editBgScale: editRecord("bgScale"),
      editBgFlip: editRecord("bgFlip"),
      editBgOpacities: editRecord("bgOpacities"),
      editBgBrightnesses: editRecord("bgBrightnesses"),
      editBgRotate: editRecord("bgRotate"),
      // Upload transforms
      editUploadX: editRecord("uploadX"),
      editUploadY: editRecord("uploadY"),
      editUploadScale: editRecord("uploadScale"),
      editUploadFlip: editRecord("uploadFlip"),
      editUploadRotate: editRecord("uploadRotate"),
      // Logo transforms
      editLogoX: editRecord("logoX"),
      editLogoY: editRecord("logoY"),
      editLogoScale: editRecord("logoScale"),
      // Records with isLocked / "main" semantics
      editLogos: editLockedRecord("logos"),
      editCustomBgs,
      editCustomBgUploaded: editRecord("customBgUploaded"),
      editUploadImages: editLockedRecord("uploadImages"),
      // Single-key record edits
      editLogoVariant: editRecord("logoVariants"),
      editTextColorLocked: editRecord("textColorLocked"),
      editButtonColor: editRecord("buttonColors"),
      editCodeHighlight: editRecord("codeHighlights"),
      // Scalars
      editCustomBg: editScalar("customBg"),
      editBgBrightness: editScalar("bgBrightness"),
      editBgBlurValue: editScalar("bgBlurValue"),
      editBgOpacity: editScalar("bgOpacity"),
      editLogoHidden: editScalar("logoHidden"),
      editIsBgAnimation: editScalar("isBgAnimation"),
      editIsUploadAnimation: editScalar("isUploadAnimation"),
    };
  }, [editRecord, editLockedRecord, editCustomBgs, editScalar]);

  const incPendingUpload = useCallback(
    () => dispatch({type: "BUMP_PENDING_UPLOADS", delta: 1}),
    []
  );
  const decPendingUpload = useCallback(
    () => dispatch({type: "BUMP_PENDING_UPLOADS", delta: -1}),
    []
  );

  const editTransform = useCallback(
    (x, y, s, r, f) => dispatch({type: "EDIT_BG_TRANSFORM", x, y, s, r, f}),
    []
  );
  const setUserBgAdjustment = useCallback(
    (bgOption, templateId, transform) =>
      dispatch({type: "SET_USER_BG_ADJUSTMENT", bgOption, templateId, transform}),
    []
  );
  const clearUserBgAdjustment = useCallback(
    (bgOption, templateId) => dispatch({type: "CLEAR_USER_BG_ADJUSTMENT", bgOption, templateId}),
    []
  );
  const editUploadTransform = useCallback(
    (x, y, s, r, f) => dispatch({type: "EDIT_UPLOAD_TRANSFORM", x, y, s, r, f}),
    []
  );
  const editLogoTransform = useCallback(
    (x, y, s) => dispatch({type: "EDIT_LOGO_TRANSFORM", x, y, s}),
    []
  );

  // Setting to undefined matches pre-refactor behavior; not deleting the
  // key. Doesn't bump uploadImageVersion (intentional — the reducer's
  // version-bump gate skips undefined values, so removals don't act as a
  // cache-buster signal).
  const removeUploadImages = useCallback(
    (id, isLocked = false) =>
      makeEditLockedRecord(dispatch, "uploadImages")(id, undefined, isLocked),
    []
  );

  const removeImages = useCallback(
    () => dispatch({type: "LOAD_RECORD", field: "uploadImages", value: {}}),
    []
  );
  const loadImages = useCallback(
    (images) => dispatch({type: "LOAD_RECORD", field: "uploadImages", value: images}),
    []
  );
  const loadCustomBgs = useCallback(
    (backgrounds) => {
      pendingCustomBgsRef.current.clear();
      const nextBackgrounds = backgrounds || {};
      stateRef.current = {...stateRef.current, customBgs: nextBackgrounds};
      dispatch({type: "LOAD_RECORD", field: "customBgs", value: nextBackgrounds});
    },
    []
  );
  const loadBgOpacities = useCallback(
    (value) => dispatch({type: "LOAD_RECORD", field: "bgOpacities", value: value || {}}),
    []
  );
  const loadBgBrightnesses = useCallback(
    (value) => dispatch({type: "LOAD_RECORD", field: "bgBrightnesses", value: value || {}}),
    []
  );
  const loadBgPresets = useCallback(
    (value) => dispatch({type: "LOAD_RECORD", field: "bgPresets", value: value || {}}),
    []
  );
  // The baked images of the applied default scene, and the bgOption they
  // belong to. One action, so the images never pair with the wrong option.
  const loadBakedBgs = useCallback(
    (bakedBgs, option) => dispatch({type: "LOAD_BAKED_BGS", bakedBgs, option}),
    []
  );
  const loadCustomBgUploaded = useCallback(
    (value) => dispatch({type: "LOAD_RECORD", field: "customBgUploaded", value: value || {}}),
    []
  );
  const loadLogos = useCallback(
    (logos) => dispatch({type: "LOAD_RECORD", field: "logos", value: logos}),
    []
  );
  const loadLogoVariants = useCallback(
    (variants) => dispatch({type: "LOAD_RECORD", field: "logoVariants", value: variants}),
    []
  );
  const loadTextColorLocked = useCallback(
    (value) => dispatch({type: "LOAD_RECORD", field: "textColorLocked", value}),
    []
  );
  const loadButtonColors = useCallback(
    (value) =>
      dispatch({type: "LOAD_RECORD", field: "buttonColors", value: normalizeButtonColors(value)}),
    []
  );

  const value = useMemo(
    () => ({
      ...state,
      ...wrappers,
      reset,
      loadScene,
      editTransform,
      setUserBgAdjustment,
      clearUserBgAdjustment,
      editUploadTransform,
      editLogoTransform,
      removeUploadImages,
      removeImages,
      loadImages,
      loadCustomBgs,
      loadBgOpacities,
      loadBgBrightnesses,
      loadBgPresets,
      loadBakedBgs,
      loadCustomBgUploaded,
      stageCustomBgsSceneDraft,
      clearCustomBgsSceneDraft,
      commitCustomBgsSceneDraft,
      readCustomBgsForScene,
      loadLogos,
      loadLogoVariants,
      loadTextColorLocked,
      loadButtonColors,
      incPendingUpload,
      decPendingUpload,
    }),
    [
      state,
      wrappers,
      reset,
      loadScene,
      editTransform,
      setUserBgAdjustment,
      clearUserBgAdjustment,
      editUploadTransform,
      editLogoTransform,
      removeUploadImages,
      removeImages,
      loadImages,
      loadCustomBgs,
      loadBgOpacities,
      loadBgBrightnesses,
      loadBgPresets,
      loadBakedBgs,
      loadCustomBgUploaded,
      stageCustomBgsSceneDraft,
      clearCustomBgsSceneDraft,
      commitCustomBgsSceneDraft,
      readCustomBgsForScene,
      loadLogos,
      loadLogoVariants,
      loadTextColorLocked,
      loadButtonColors,
      incPendingUpload,
      decPendingUpload,
    ]
  );

  return <Images.Provider value={value}>{children}</Images.Provider>;
};

export function useImages() {
  const context = useContext(Images);
  if (context === undefined) {
    throw new Error("Images.Context must be used within the Images.Provider");
  }
  return context;
}
